import { useQueryState } from 'nuqs';
import { useRef, useState } from 'react';
import { isEmptyString } from '@/utils/string';
import { useMountEffect } from './use-mount-effect';

/**
 * Executes an action once when a query parameter is present on mount.
 * Automatically clears the query parameter after executing the action.
 *
 * This is useful for triggering modals or other side effects based on URL params
 * without re-triggering on subsequent renders.
 *
 * @param paramName - The query parameter name
 * @param action - Function to execute with the query param value
 *
 * @example
 * useQueryParamAction('support', () => {
 *   openSupportForm();
 * }); // Triggers when ?support=true (or any value), ignores the value
 *
 * @example
 * useQueryParamAction('ai-message', (message) => {
 *   openChatWithMessage(message);
 * }); // Triggers when ?ai-message=anything and passes the value
 */
export function useQueryParamAction(parameterName: string, action: (value: string) => void): void {
  const [parameterValue, setParameterValue] = useQueryState(parameterName);

  // Capture initial value once on mount
  const [initialValue] = useState(() => parameterValue ?? '');
  const triggered = useRef(false);

  useMountEffect(() => {
    if (!isEmptyString(initialValue) && !triggered.current) {
      triggered.current = true;
      void setParameterValue(null);
      action(initialValue);
    }
  });
}
